import { z } from 'zod';

import { ProductKind, SubscriptionDTO } from './me';
import { I18nText, VoucherCode } from './primitives';

export const PriceInterval = z.enum(['month', 'year', 'one_time']);
export type PriceInterval = z.infer<typeof PriceInterval>;

export const Currency = z.string().regex(/^[A-Z]{3}$/, 'ISO-4217 code');
export type Currency = z.infer<typeof Currency>;

export const PriceDTO = z.object({
  id: z.string(),
  interval: PriceInterval,
  amountMinor: z.number().int().nonnegative(),
  currency: Currency,
});
export type PriceDTO = z.infer<typeof PriceDTO>;

/** Active product with its active prices (`GET /billing/products`). */
export const ProductDTO = z.object({
  id: z.string(),
  kind: ProductKind,
  name: I18nText,
  description: I18nText,
  features: z.array(z.string()),
  requiresPlan: z.boolean(),
  prices: z.array(PriceDTO),
});
export type ProductDTO = z.infer<typeof ProductDTO>;

export const ProductsResponse = z.object({ products: z.array(ProductDTO) });
export type ProductsResponse = z.infer<typeof ProductsResponse>;

/** `POST /billing/checkout` */
export const CheckoutBody = z.object({
  priceId: z.string().min(1),
  addonPriceIds: z.array(z.string().min(1)).max(10).default([]),
  /**
   * App path (with query and hash if any) the user returns to after Stripe Checkout. It resolves
   * against `WEB_BASE`, and an absolute URL is only accepted on that same origin.
   */
  returnTo: z.string().min(1).max(2048),
  /** Embedded Checkout, inside a modal of the site: answers `clientSecret` instead of `url`. */
  embedded: z.boolean().default(false),
});
export type CheckoutBody = z.infer<typeof CheckoutBody>;
export type CheckoutBodyInput = z.input<typeof CheckoutBody>;

export const CheckoutResponse = z.union([
  z.object({ url: z.string().url() }),
  z.object({ clientSecret: z.string().min(1) }),
]);
export type CheckoutResponse = z.infer<typeof CheckoutResponse>;

/** `POST /billing/portal` */
export const PortalBody = z.object({ returnTo: z.string().min(1).max(2048).optional() });
export type PortalBody = z.infer<typeof PortalBody>;

export const PortalResponse = z.object({ url: z.string().url() });
export type PortalResponse = z.infer<typeof PortalResponse>;

/** `POST /billing/voucher` */
export const VoucherBody = z.object({ code: VoucherCode });
export type VoucherBody = z.infer<typeof VoucherBody>;
export type VoucherBodyInput = z.input<typeof VoucherBody>;

export const VoucherResponse = z.object({ subscription: SubscriptionDTO });
export type VoucherResponse = z.infer<typeof VoucherResponse>;

/** `POST /webhooks/stripe` (raw body, signature-verified) */
export const StripeWebhookResponse = z.object({ received: z.literal(true) });
export type StripeWebhookResponse = z.infer<typeof StripeWebhookResponse>;

/** Stripe event types the webhook handles (docs/03 §2). */
export const STRIPE_EVENT_TYPES = [
  'checkout.session.completed',
  'customer.subscription.created',
  'customer.subscription.updated',
  'customer.subscription.deleted',
  'invoice.paid',
  'invoice.payment_failed',
  'charge.refunded',
] as const;
export type StripeEventType = (typeof STRIPE_EVENT_TYPES)[number];
